import Header from "@/components/navigation/Header";
import Footer from "@/components/sections/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, BookOpen } from "lucide-react";
import { useState } from "react";

const Glossary = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const glossaryTerms = [
    {
      term: "Bitcoin (BTC)",
      definition: "A decentralized digital currency created in 2009 by an unknown person using the name Satoshi Nakamoto. Bitcoin operates without a central bank or single administrator.",
      category: "Basic Concepts",
      relatedTerms: ["Cryptocurrency", "Blockchain", "Satoshi"]
    },
    {
      term: "Blockchain",
      definition: "A distributed ledger technology that maintains a continuously growing list of records (blocks) linked and secured using cryptography.",
      category: "Technology",
      relatedTerms: ["Bitcoin", "Hash", "Block"]
    },
    {
      term: "Cold Storage",
      definition: "A method of storing Bitcoin private keys offline, typically on hardware wallets or paper wallets, to protect against hacking and theft.",
      category: "Security",
      relatedTerms: ["Hot Wallet", "Private Key", "Hardware Wallet"]
    },
    {
      term: "DCA (Dollar Cost Averaging)",
      definition: "An investment strategy where you purchase Bitcoin at regular intervals regardless of price, reducing the impact of volatility over time.",
      category: "Investment Strategy",
      relatedTerms: ["Investment Strategy", "Volatility", "Portfolio"]
    },
    {
      term: "HODL",
      definition: "A cryptocurrency slang term meaning to hold onto Bitcoin for long periods, originally a misspelling of 'hold' that became popular in the Bitcoin community.",
      category: "Culture",
      relatedTerms: ["Investment Strategy", "Bitcoin Community"]
    },
    {
      term: "Hardware Wallet",
      definition: "A physical device designed to securely store Bitcoin private keys offline, providing enhanced security compared to software wallets.",
      category: "Security",
      relatedTerms: ["Cold Storage", "Private Key", "Security"]
    },
    {
      term: "Hash Rate",
      definition: "The computational power used in Bitcoin mining and processing transactions, measured in hashes per second.",
      category: "Mining",
      relatedTerms: ["Mining", "Network Security", "Difficulty"]
    },
    {
      term: "Lightning Network",
      definition: "A second-layer payment protocol built on top of Bitcoin that enables fast, low-cost transactions through payment channels.",
      category: "Technology",
      relatedTerms: ["Scalability", "Payment Channels", "Layer 2"]
    },
    {
      term: "Multi-signature (Multisig)",
      definition: "A Bitcoin wallet that requires multiple private keys to authorize transactions, providing enhanced security and shared control.",
      category: "Security",
      relatedTerms: ["Private Key", "Security", "Wallet"]
    },
    {
      term: "Node",
      definition: "A computer that maintains a copy of the Bitcoin blockchain and helps validate and relay transactions across the network.",
      category: "Technology",
      relatedTerms: ["Blockchain", "Network", "Validation"]
    },
    {
      term: "Private Key",
      definition: "A secret cryptographic key that allows you to spend Bitcoin from a specific address. Must be kept secure and never shared.",
      category: "Security",
      relatedTerms: ["Public Key", "Address", "Security"]
    },
    {
      term: "Proof of Work (PoW)",
      definition: "Bitcoin's consensus mechanism that requires miners to solve complex mathematical puzzles to validate transactions and secure the network.",
      category: "Technology",
      relatedTerms: ["Mining", "Consensus", "Network Security"]
    },
    {
      term: "Satoshi (sat)",
      definition: "The smallest unit of Bitcoin, equal to 0.00000001 BTC. Named after Bitcoin's pseudonymous creator, Satoshi Nakamoto.",
      category: "Basic Concepts",
      relatedTerms: ["Bitcoin", "Units", "Satoshi Nakamoto"]
    },
    {
      term: "Wallet",
      definition: "Software or hardware that stores Bitcoin private keys and allows users to send, receive, and manage their Bitcoin.",
      category: "Basic Concepts",
      relatedTerms: ["Private Key", "Address", "Security"]
    },
    {
      term: "Yield Farming",
      definition: "The practice of lending or staking Bitcoin or other cryptocurrencies to earn additional returns through interest or rewards.",
      category: "Investment Strategy",
      relatedTerms: ["DeFi", "Lending", "Staking"]
    }
  ];

  const categories = ["All", "Basic Concepts", "Technology", "Security", "Investment Strategy", "Mining", "Culture"];

  const filteredTerms = glossaryTerms.filter(term =>
    term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
    term.definition.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white/10 rounded-2xl">
                <BookOpen className="h-12 w-12 text-bitcoin-orange" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Bitcoin Glossary
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-8">
              Comprehensive definitions of Bitcoin and cryptocurrency terms for real estate 
              investors and professionals entering the digital asset space.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input 
                placeholder="Search terms..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/60"
              />
            </div>
          </div>
        </div>
      </section>

      <main className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-12 justify-center">
            {categories.map((category) => (
              <Badge 
                key={category} 
                variant={category === "All" ? "default" : "outline"}
                className="cursor-pointer hover:bg-bitcoin-orange hover:text-white transition-colors"
              >
                {category}
              </Badge>
            ))}
          </div>

          {/* Results Count */}
          <div className="mb-8">
            <p className="text-muted-foreground">
              {filteredTerms.length} {filteredTerms.length === 1 ? 'term' : 'terms'} found
              {searchTerm && ` for "${searchTerm}"`}
            </p>
          </div>

          {/* Glossary Terms */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredTerms.map((item, index) => (
              <Card key={index} className="h-full hover:shadow-soft transition-smooth">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-xl font-bold text-bitcoin-orange">
                      {item.term}
                    </CardTitle>
                    <Badge variant="outline" className="text-xs">
                      {item.category}
                    </Badge>
                  </div>
                  <CardDescription className="text-base leading-relaxed">
                    {item.definition}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div>
                    <h4 className="text-sm font-semibold text-muted-foreground mb-2">
                      Related Terms:
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {item.relatedTerms.map((relatedTerm, relatedIndex) => (
                        <Badge key={relatedIndex} variant="secondary" className="text-xs">
                          {relatedTerm}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* No Results */}
          {filteredTerms.length === 0 && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-foreground mb-2">No terms found</h3>
              <p className="text-muted-foreground">
                Try adjusting your search or browse all terms above.
              </p>
            </div>
          )}

          {/* CTA Section */}
          <section className="mt-20 bg-muted rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Need Help Understanding Bitcoin?
            </h3>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Our Bitcoin experts can help you navigate the complexities of cryptocurrency 
              and develop a strategy that makes sense for your real estate investment goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-bitcoin-orange text-white px-8 py-3 rounded-lg font-semibold hover:bg-bitcoin-orange/90 transition-colors">
                Schedule Consultation
              </button>
              <button className="border border-border text-foreground px-8 py-3 rounded-lg font-semibold hover:bg-muted-foreground/10 transition-colors">
                Read Our Blog
              </button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Glossary;